import { useState } from 'react';

import { ApiError, moveStage, type StageDetail, type StageStatus } from '../api.js';
import { STAGE_LABELS, STAGE_STATUS, formatDate } from '../labels.js';
import { GATE_TARGETS, gateHref } from './project-sections.js';
import { AppLink } from '../link.js';
import { useProjectStagesState } from './ProjectStages.js';

/**
 * The eight lifecycle stages, and the moves the caller may make.
 *
 * The stages OVERLAP by design (ADR-002) — design can still be running while
 * procurement starts — so each carries its own status rather than there being a
 * single "current stage" pointer, which would be a lie on most live projects.
 *
 * Which buttons appear is decided by the SERVER (`allowed_next`), not by rules
 * repeated here. Some moves belong to the system alone: a stage becomes
 * "at risk" because the Pulse engine says so, never because someone clicked.
 */

const MIN_REASON_LENGTH = 20;

/**
 * What the button should say, in the language of the job.
 *
 * It depends on where the stage is coming FROM, not only where it is going:
 * `→ active` is "Start" from not-started, "Unblock" from blocked, and "Reopen"
 * from complete. One label for the destination would call reopening a finished
 * stage "Start", which is how someone undoes a completion by accident.
 */
function moveLabel(from: StageStatus, to: StageStatus): string {
  if (to === 'active') {
    if (from === 'complete') return 'Reopen';
    if (from === 'blocked' || from === 'at_risk') return 'Unblock';
    return 'Start';
  }
  switch (to) {
    case 'complete':
      return 'Complete';
    case 'blocked':
      return 'Block';
    case 'at_risk':
      return 'Flag for attention';
    case 'not_started':
      return 'Reset';
  }
}

interface Props {
  readonly projectId: string;
  /** Financial access can change with a stage move, so the page re-reads. */
  readonly onChanged: () => void;
}

export function Lifecycle({ projectId, onChanged }: Props): JSX.Element {
  // The page's one stages fetch, shared with the "Needs you now" panel — which
  // reads the SAME gate items, so the two must never be able to disagree. On
  // its own (a screen test, a future page) the hook fetches for itself.
  const { state, replace } = useProjectStagesState(projectId);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The stage whose detail is showing. `null` means "whichever one the project
   * is actually on" — see `defaultStage` below. Storing null rather than the
   * computed key is what lets the default FOLLOW the project: complete a stage
   * and the panel moves to the next one, without a click.
   */
  const [open, setOpen] = useState<string | null>(null);
  /** A move waiting for a reason: which stage, and where it is going. */
  const [asking, setAsking] = useState<{ key: string; to: StageStatus } | null>(null);
  const [reason, setReason] = useState('');

  async function attempt(key: string, to: StageStatus, why?: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // The server hands back the whole new list, so the shared state is
      // replaced rather than re-fetched.
      replace(await moveStage(projectId, key, { status: to, ...(why ? { reason: why } : {}) }));
      setAsking(null);
      setReason('');
      onChanged();
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : 'That did not work.';
      setError(message);
      // The server refuses without a reason before it refuses anything else, so
      // a refusal mentioning one is an invitation to give one, not a dead end.
      if (/reason/i.test(message)) {
        setAsking({ key, to });
      }
    } finally {
      setBusy(false);
    }
  }

  if (state.status === 'loading') {
    return (
      <>
        <h3 className="sub-head">Lifecycle</h3>
        <p aria-live="polite">Loading the lifecycle…</p>
      </>
    );
  }

  if (state.status === 'not_permitted') {
    return (
      <>
        <h3 className="sub-head">Lifecycle</h3>
        <p className="hint">Your role does not include this project&rsquo;s stages.</p>
      </>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        <h3 className="sub-head">Lifecycle</h3>
        <div className="alert" role="alert">
          <span>{state.message}</span>
        </div>
      </>
    );
  }

  /**
   * The stage to show when nobody has picked one.
   *
   * The one being worked on: the first that is running, blocked or at risk;
   * failing that the first not yet started, so a fresh project opens on the
   * stage it is about to begin; failing that the last one, on a finished job.
   *
   * This matters more than it looks. Whatever this picks is what a Project
   * Manager sees the moves for without clicking, so it has to be the stage
   * they came to the page for.
   */
  const inPlay = state.stages.find((s) => ['active', 'blocked', 'at_risk'].includes(s.status));
  const notStarted = state.stages.find((s) => s.status === 'not_started');
  const defaultStage = inPlay ?? notStarted ?? state.stages[state.stages.length - 1];
  const selected = state.stages.find((s) => s.stage_key === open) ?? defaultStage;
  const selectedKey = selected?.stage_key;

  return (
    <>
      <h3 className="sub-head">Lifecycle</h3>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {/*
        A STRIP, and then ONE stage's detail underneath it.

        This used to be eight cards stacked down the page, each carrying its own
        buttons and its own collapsed gate — 1,151 pixels of a 4,876-pixel
        screen, measured. Eight sets of actions were on screen and at most one
        of them was the one you wanted.

        Now the eight are a strip you read in a glance, and the stage you are
        working on shows its dates, its moves and its checks below. The strip
        keeps the symbol AND the word on every step, so status survives a
        printout and a screen reader, and the selected one is marked with
        `aria-current` rather than by colour alone.
      */}
      <ol className="stepper">
        {state.stages.map((stage, index) => {
          const look = STAGE_STATUS[stage.status] ?? { symbol: '·', label: stage.status };
          const name = STAGE_LABELS[stage.stage_key] ?? stage.stage_key;
          const isSelected = stage.stage_key === selectedKey;

          return (
            <li key={stage.stage_key} className={`step step-${stage.status}`}>
              <button
                type="button"
                className={isSelected ? 'step-button is-selected' : 'step-button'}
                aria-current={isSelected ? 'step' : undefined}
                onClick={() => setOpen(stage.stage_key)}
              >
                <span className="step-index" aria-hidden="true">
                  {index + 1}
                </span>
                <span className="step-name">{name}</span>
                <span className="step-state">
                  <span className="step-symbol" aria-hidden="true">
                    {look.symbol}
                  </span>{' '}
                  {look.label}
                </span>
                {stage.is_overridden && <span className="step-flag">Overridden</span>}
              </button>
            </li>
          );
        })}
      </ol>

      {selected && (
        <div className={`stage-detail stage-${selected.status}`}>
          <div className="stage-detail-head">
            <h4>{STAGE_LABELS[selected.stage_key] ?? selected.stage_key}</h4>
            <span className="stage-detail-state">
              {(STAGE_STATUS[selected.status] ?? { label: selected.status }).label}
            </span>

            <span className="stage-actions">
              {selected.allowed_next.map((to) => (
                <button
                  key={to}
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => void attempt(selected.stage_key, to)}
                >
                  {moveLabel(selected.status, to)}
                  <span className="visually-hidden">
                    {' '}
                    {STAGE_LABELS[selected.stage_key] ?? selected.stage_key}
                  </span>
                </button>
              ))}
            </span>
          </div>

          {asking?.key === selected.stage_key && (
            <div className="stage-reason">
              <label htmlFor={`reason-${selected.stage_key}`}>
                Why? This is recorded permanently.
              </label>
              <textarea
                id={`reason-${selected.stage_key}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={2000}
                disabled={busy}
              />
              <p className="hint">
                {reason.trim().length}/{MIN_REASON_LENGTH} characters minimum.
              </p>
              <div className="button-row">
                <button
                  type="button"
                  className="inline"
                  disabled={busy || reason.trim().length < MIN_REASON_LENGTH}
                  onClick={() => void attempt(asking.key, asking.to, reason.trim())}
                >
                  {busy ? 'Saving…' : `${moveLabel(selected.status, asking.to)} anyway`}
                </button>
                <button
                  type="button"
                  className="secondary inline"
                  disabled={busy}
                  onClick={() => {
                    setAsking(null);
                    setReason('');
                    setError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/*
            * Why a stage is at risk, said before the checklist.
            *
            * The app moved this stage, not a colleague, and that is the first
            * thing to say — a status that changed overnight with no author is
            * the single most unsettling thing software does to a site team.
            * The user asked for the automatic move precisely because people
            * forget; the price of that is owning it in plain words here.
            */}
          {selected.status === 'at_risk' && selected.risk_reason && (
            <p className="stage-risk">
              <strong>Flagged automatically.</strong> {selected.risk_reason}
              {selected.risk_since && ` — since ${formatDate(selected.risk_since)}`}. Nobody
              changed this by hand. It clears itself when the problem is fixed.
            </p>
          )}

          {/* Always open, no longer behind a "Checks" button. The gate IS the
              stage — one stage's worth is a handful of lines, and hiding it was
              what made people press Complete to find out what it wanted. */}
          <GateList stage={selected} projectId={projectId} />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function GateList({
  stage,
  projectId,
}: {
  readonly stage: StageDetail;
  readonly projectId: string;
}): JSX.Element {
  const notCheckable = stage.gate.items.filter((i) => i.status === 'not_checkable');

  return (
    <div className="stage-gate">
      <ul className="gate-list">
        {stage.gate.items.map((item) => {
          // Only an UNSATISFIED item gets a link. A satisfied one is a fact,
          // not a job, and eleven ticks each offering to take you somewhere is
          // how a checklist stops being read.
          const target = item.status === 'unsatisfied' ? GATE_TARGETS[item.key] : undefined;
          return (
            <li key={item.key} className={`gate-item gate-${item.status}`}>
              <span className="gate-mark" aria-hidden="true">
                {item.status === 'satisfied' ? '✓' : item.status === 'unsatisfied' ? '✗' : '–'}
              </span>
              <span>
                {item.label}
                <span className="visually-hidden">
                  {item.status === 'satisfied'
                    ? ' — done'
                    : item.status === 'unsatisfied'
                      ? ' — not done'
                      : ' — cannot be checked yet'}
                </span>
                {target && (
                  <>
                    {' '}
                    <AppLink className="gate-go" to={gateHref(projectId, target)}>
                      Go to {target.action}
                      <span className="visually-hidden"> — {item.label}</span>
                    </AppLink>
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {notCheckable.length > 0 && (
        // Said plainly rather than hidden: these checks are specified but the
        // part of the product they read does not exist, so they let a stage
        // through. Pretending they passed would be the dishonest option.
        <p className="hint">
          {notCheckable.length} check{notCheckable.length === 1 ? '' : 's'} cannot run yet — waiting
          on {[...new Set(notCheckable.map((i) => i.waiting_for))].join(', ')}. They do not stop the
          stage being completed.
        </p>
      )}

      {stage.override_reason && (
        <p className="hint">
          <strong>Completed anyway.</strong> {stage.override_reason}
        </p>
      )}

      {(stage.started_at ?? stage.completed_at) && (
        <p className="hint">
          {stage.started_at && `Started ${formatDate(stage.started_at)}. `}
          {stage.completed_at && `Completed ${formatDate(stage.completed_at)}. `}
          {stage.reopened_at && `Reopened ${formatDate(stage.reopened_at)}.`}
        </p>
      )}

      {/*
        WHEN THE APP STARTED IT, SAY SO.

        A stage can now start itself the moment work appears in its register
        (ADR-043), and the rule inherited from `stage.at_risk` governs it: it
        must be VISIBLE that the app did it and not a colleague. A date alone
        cannot carry that — "Started 31 Aug" reads identically whoever caused it
        — so the stage's own sentence goes under it.

        Absent for every stage a person started, which is most of them.
      */}
      {stage.started_reason && <p className="hint">{stage.started_reason}</p>}
    </div>
  );
}
